import { memo } from 'react';
import { ExternalLink, Loader, Database } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { PackEnrichmentResponse } from '../../types/apogee';
import { cn } from '../../utils/cn';
import { SkeletonLoader } from '../SkeletonLoader';
import { SectionHeader } from '../ui/SectionHeader';

interface PackEnrichmentSectionProps {
  enrichment: PackEnrichmentResponse | null;
  loading: boolean;
  error?: string | null;
  className?: string;
}

const modrinthProjectUrl = (slug: string) => `https://modrinth.com/mod/${encodeURIComponent(slug)}`;

export const PackEnrichmentSection = memo(({
  enrichment,
  loading,
  error,
  className,
}: PackEnrichmentSectionProps) => {
  if (loading) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)} aria-busy="true">
        <SectionHeader
          title="Platform enrichment"
          icon={Database}
          actions={<Loader className="w-4 h-4 animate-spin text-accent-300" />}
        />
        <div className="grid gap-4 md:grid-cols-2">
          <SkeletonLoader className="h-28 w-full rounded-lg" />
          <SkeletonLoader className="h-28 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)}>
        <SectionHeader title="Platform enrichment" icon={Database} />
        <p className="text-sm text-glass-500">{error}</p>
      </div>
    );
  }

  if (!enrichment) return null;

  const hasAny = enrichment.modrinth != null || enrichment.curseforge != null;

  return (
    <div className={cn('glass-card p-4 md:p-5', className)}>
      <SectionHeader
        title="Platform enrichment"
        icon={Database}
        description="Modrinth & CurseForge snapshots"
      />

      {!hasAny && (
        <p className="text-sm text-glass-400 mb-4">
          No enrichment data yet. Run ingest enrichment jobs (<code className="text-xs text-glass-500">modrinth_enrich</code>,{' '}
          <code className="text-xs text-glass-500">curseforge_enrich</code>) to populate authors and descriptions.
        </p>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {enrichment.modrinth ? (
          <div className="subcard p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="eyebrow text-green-400">Modrinth</span>
              <a
                href={modrinthProjectUrl(enrichment.modrinth.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="link inline-flex items-center gap-1 text-xs"
              >
                Open <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <p className="text-sm font-medium text-white">{enrichment.modrinth.name}</p>
            <p className="text-xs font-mono text-glass-500">
              {enrichment.modrinth.slug} · {enrichment.modrinth.project_id}
            </p>
            {enrichment.modrinth.author && (
              <p className="text-sm text-glass-300">
                <span className="text-glass-500">Author</span> {enrichment.modrinth.author}
              </p>
            )}
            {enrichment.modrinth.description && (
              <p className="text-sm text-glass-400 line-clamp-4 whitespace-pre-wrap">{enrichment.modrinth.description}</p>
            )}
            <p className="text-xs text-glass-500 pt-1 tabular-nums">
              Observed {formatDistanceToNow(new Date(enrichment.modrinth.observed_at), { addSuffix: true })}
              <span className="text-glass-600"> · {format(new Date(enrichment.modrinth.observed_at), 'yyyy-MM-dd HH:mm')}</span>
            </p>
          </div>
        ) : (
          <div className="subcard border-dashed p-4 flex items-center justify-center min-h-[120px]">
            <p className="text-sm text-glass-500 text-center">No Modrinth enrichment snapshot</p>
          </div>
        )}

        {enrichment.curseforge ? (
          <div className="subcard p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="eyebrow text-orange-400">CurseForge</span>
              <span className="text-xs font-mono text-glass-400 tabular-nums">mod {enrichment.curseforge.cf_mod_id}</span>
            </div>
            {enrichment.curseforge.author && (
              <p className="text-sm text-glass-300">
                <span className="text-glass-500">Author</span> {enrichment.curseforge.author}
              </p>
            )}
            {enrichment.curseforge.description && (
              <p className="text-sm text-glass-400 line-clamp-4 whitespace-pre-wrap">{enrichment.curseforge.description}</p>
            )}
            <p className="text-xs text-glass-500 pt-1 tabular-nums">
              Observed {formatDistanceToNow(new Date(enrichment.curseforge.observed_at), { addSuffix: true })}
              <span className="text-glass-600"> · {format(new Date(enrichment.curseforge.observed_at), 'yyyy-MM-dd HH:mm')}</span>
            </p>
          </div>
        ) : (
          <div className="subcard border-dashed p-4 flex items-center justify-center min-h-[120px]">
            <p className="text-sm text-glass-500 text-center">No CurseForge enrichment snapshot</p>
          </div>
        )}
      </div>
    </div>
  );
});

PackEnrichmentSection.displayName = 'PackEnrichmentSection';
